import React from 'react';
import Joi from 'joi';
import {Select, RadioGroup} from 'components/forms/fields';
import Section from 'components/forms/Section';
import useFieldData from 'components/forms/hooks/useFieldData';
import {Typography} from '@material-ui/core';
import {useContext} from 'react';
import FormState from '../context/FormState';
import useFormPersistence from '../hooks/useFormPersistence';

const deckPositions = {
    Master: 'Master',
    'Chief Mate / 1st Mate': 'Chief Mate / 1st Mate',
    '2nd Mate': '2nd Mate',
    '3rd Mate': '3rd Mate',
    Mate: 'Mate',
    'Master of Towing': 'Master of Towing',
    'Mate Pilot of Towing': 'Mate Pilot of Towing',
    'Apprentice Mate (Steersman)': 'Apprentice Mate (Steersman)',
    'Mate (Trainee)': 'Mate (Trainee)',
    'First Class Pilot': 'First Class Pilot',
    'Apprentice Pilot': 'Apprentice Pilot',
    'OUPV Operator': 'OUPV Operator',
    'Able Seaman': 'Able Seaman',
    Boatswain: 'Boatswain',
    Quartermaster: 'Quartermaster',
    'Deck Hand': 'Deck Hand',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const enginePositions = {
    'Chief Engineer': 'Chief Engineer',
    '1st Assistant Engineer': '1st Assistant Engineer',
    '2nd Assistant Engineer': '2nd Assistant Engineer',
    '3rd Assistant Engineer': '3rd Assistant Engineer',
    'Assistant Engineer': 'Assistant Engineer',
    'Designated Duty Engineer': 'Designated Duty Engineer',
    Engineer: 'Engineer',
    'Junior Unlicensed Engineer': 'Junior Unlicensed Engineer',
    'Junior Engineer': 'Junior Engineer',
    'Qualified Member of the Engine Department (QMED)':
        'Qualified Member of the Engine Department (QMED)',
    'Electrician-Refrigerating Engineer': 'Electrician-Refrigerating Engineer',
    Oiler: 'Oiler',
    'Fireman-Watertender': 'Fireman-Watertender',
    'Machinist-Pumpman': 'Machinist-Pumpman',
    Wiper: 'Wiper',
    'Crew Member': 'Crew Member',
    'Entry Level': 'Entry Level',
};

const schema = {
    department: Joi.string().max(20).required(),
    deck_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('department'), {
            is: 'Deck',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
    engine_position_served: Joi.string()
        .max(100)
        .when(Joi.ref('department'), {
            is: 'Engine',
            then: Joi.string().required(),
            otherwise: Joi.string().allow(''),
        }),
};

const initialValues = {
    department: '',
    deck_position_served: '',
    engine_position_served: '',
};

const Department = props => {
    const {node} = useContext(FormState);
    const {state, updateState} = useFormPersistence(node);
    const [department, setDepartment] = useFieldData('department');
    const updateDepartment = e => {
        // Getting data from radio, probably should extract this pattern into a HOC
        setDepartment(e.currentTarget.value);
        updateState({
            ...state,
            deck_position_served: '',
            engine_position_served: '',
        });
    };

    return (
        <Section {...props} {...{schema, initialValues}}>
            <Typography variant='h6' align='left'>
                Position
            </Typography>
            <Typography variant='body1'>
                Please select the department and position you 'Served As' from the
                options below. If the position you listed on your form is not one of
                the available selections, please select the closest option and write
                in the position you served as below.
            </Typography>
            <RadioGroup
                name='department'
                onChange={updateDepartment}
                label='Did you serve in the Deck Department or the Engine Department?'
                required
                options={{
                    Deck: 'Deck Department',
                    Engine: 'Engine Department',
                }}
            />
            <Select
                required
                name={`${department.toLowerCase()}_position_served`}
                label={
                    department
                        ? `${department} Positions`
                        : 'Please select a department'
                }
                options={
                    department === 'Engine'
                        ? enginePositions
                        : department === 'Deck'
                        ? deckPositions
                        : {empty: 'Please select a department'}
                }
            />
            {props.children}
        </Section>
    );
};

export default Department;
